// src/components/FeedList/FeedList.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ko";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation } from "react-router-dom";

import { AppDispatch, RootState } from "../../store/store";
import { fetchFeedsStart } from "../../store/slice/feedSlice";
import LoadingSpinner from "../LoadingSpinner";
import FeedDetailDialog from "../detail/FeedDetailDialog";
import EmptyState from "./EmptyState";
import { normalizeForDetail } from "../../utils/normalizeForDetail";
import { FeedItem } from "../../types/FeedItem";
import { useFeedFilters } from "../../hooks/useFeedFilters";

import "./feedList.modern-table.css";

dayjs.extend(relativeTime);
dayjs.locale("ko");

type MappingMode = "all" | "direct" | "review" | "watch" | "radar" | "none";
type CandidateGroup = "direct" | "review" | "watch";
type Tone = "direct" | "review" | "watch" | "risk" | "neutral" | "positive";

type StockCandidate = {
  name?: string;
  corp_name?: string;
  corpName?: string;
  ticker?: string;
  stock_code?: string;
  stockCode?: string;
  code?: string;
  tier?: string;
  mapping_bucket?: string;
  mappingBucket?: string;
  mapping_decision?: string;
  mappingDecision?: string;
  decision?: string;
  relation_tier?: string;
  reason?: string;
  reason_code?: string;
  role?: string;
  match_type?: string;
  matchType?: string;
  matched_terms?: any;
  matchedTerms?: any;
  strong_terms?: any;
  strongTerms?: any;
  support_terms?: any;
  supportTerms?: any;
  user_visible?: boolean;
  is_direct_stock_mapping?: boolean;
  isDirectStockMapping?: boolean;
  score?: number;
  mapping_score?: number;
  final_score?: number;
  [key: string]: any;
};

type CandidateBuckets = {
  direct: StockCandidate[];
  review: StockCandidate[];
  watch: StockCandidate[];
  visible: StockCandidate[];
};

type FeedViewModel = {
  id: string;
  feed: FeedItem;
  title: string;
  summary: string;
  source: string;
  date: string;
  dateLabel: string;
  sectorLabel: string;
  canonicalSector: string;
  tags: string[];
  impactScore: number;
  impactDirection: "positive" | "negative" | "neutral";
  impactLabel: string;
  impactReason: string;
  modeLabel: string;
  tone: Tone;
  buckets: CandidateBuckets;
  topCandidates: StockCandidate[];
  priority: number;
  clusterKey: string;
};

type FeedCluster = {
  key: string;
  rep: FeedViewModel;
  count: number;
  others: FeedViewModel[];
};



const INTERNAL_REASON_CODES = new Set([
  "EVENT_UNIT_PUBLIC_REVIEW_BLOCKED_BY_WEAK_ENTITY_EVIDENCE",
  "AMBIGUOUS_ACTOR_COMPANY_MATCH",
  "EVENT_UNIT_STOCK_MAPPING_DISABLED",
  "POLICY_EVENT_EXCLUDE_CAPPED",
  "POLICY_EVENT_WATCH_CAPPED",
  "POLICY_EVENT_REVIEW_CAPPED",
]);

function parseJsonLoose(value: any): any {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cleanText(value: any): string {
  return String(value ?? "")
    .replace(/&amp;/g, "&")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function firstNonEmpty(...values: any[]): any {
  for (const value of values) {
    if (cleanText(value)) return value;
  }
  return "";
}



function asArrayLoose(value: any): any[] {
  const parsed = parseJsonLoose(value);
  const source = parsed ?? value;
  if (Array.isArray(source)) return source;
  return [];
}

function normalizeTicker(value: any): string | undefined {
  const raw = String(value ?? "").trim().toUpperCase();
  if (!raw) return undefined;
  const code = raw.match(/^A?(\d{6})$/)?.[1] ?? raw.match(/\b(\d{6})\b/)?.[1];
  if (!code || code === "000000" || code === "999999") return undefined;
  return code;
}

function clamp100(value: any): number {
  if (typeof value === "string") {
    const m = value.match(/-?\d+(?:\.\d+)?/);
    if (m) value = Number(m[0]);
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

function norm(value: any): string {
  return cleanText(value)
    .replace(/[\s_\-/]+/g, " ")
    .toLowerCase();
}

function stripBoilerplate(value: any): string {
  return cleanText(value)
    .replace(/^\[\s*분석\s*대상\s*기사\s*\]\s*/i, "")
    .replace(/^제목\s*[:：]\s*/i, "")
    .replace(/\s*원본\s*URL\s*[:：].*$/i, "")
    .replace(/\s*URL\s*[:：]\s*https?:\/\/\S+.*$/i, "")
    .replace(/\s*발행일\s*[:：]\s*\d{4}[-.]\d{1,2}[-.]\d{1,2}[^출처요약]*/i, " ")
    .replace(/\s*출처\s*[:：]\s*[^요약]{1,90}(?=\s*(?:기준\s*)?요약\s*[:：]|$)/i, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractTitle(value: any): string {
  const raw = stripBoilerplate(value);
  if (!raw) return "";
  return cleanText(
    raw.match(
      /(?:제목\s*[:：]\s*)?(.+?)(?=\s*(?:기준\s*)?요약\s*[:：]|\s*출처\s*[:：]|\s*발행일\s*[:：]|\s*원본\s*URL\s*[:：]|$)/i,
    )?.[1] ?? raw,
  )
    .replace(/\s*(?:기준\s*)?요약\s*[:：].*$/i, "")
    .replace(/\s*출처\s*[:：].*$/i, "")
    .replace(/\s*발행일\s*[:：].*$/i, "")
    .trim();
}

function extractSummary(value: any): string {
  const raw = stripBoilerplate(value);
  if (!raw) return "";
  return cleanText(
    raw.match(
      /(?:기준\s*)?요약\s*[:：]\s*(.+?)(?=\s*출처\s*[:：]|\s*발행일\s*[:：]|\s*원본\s*URL\s*[:：]|$)/i,
    )?.[1] ?? raw,
  )
    .replace(/^제목\s*[:：].+?(?:기준\s*)?요약\s*[:：]\s*/i, "")
    .replace(/\s*출처\s*[:：].*$/i, "")
    .replace(/\s*발행일\s*[:：].*$/i, "")
    .replace(/\s*원본\s*URL\s*[:：].*$/i, "")
    .trim();
}

function isInternalLine(value: any): boolean {
  const text = cleanText(value);
  const normalized = norm(text);
  if (!text) return true;
  if (/source_classification|event_final_mode|event_mapping_status|mapping_status|candidate_summary|candidate_recall|ticker_buckets|debug|trace/i.test(text)) return true;
  if (/stock_business_profile|business_role_summary_json|evidence_json|impact_json_raw|reason_code|deterministic|precision gate|direct_entity_lookup|profile_recall/i.test(text)) return true;
  if (/^(stock mapped|sector only|macro only|no actionable mapping|event action|event review|event watch)$/i.test(normalized)) return true;
  return false;
}

function publicText(value: any): string {
  const text = cleanText(value)
    .replace(/DIRECT_ENTITY_BUSINESS_PROFILE_MATERIAL_MATCHED/gi, "직접 영향")
    .replace(/DIRECT_ENTITY_BUSINESS_PROFILE_REVIEW_MATCHED/gi, "검토 후보")
    .replace(/DIRECT_ENTITY_ECONOMIC_BRIDGE_PROFILE_REVIEW/gi, "검토 후보")
    .replace(/DIRECT_ENTITY_PROFILE_WEAK_WATCH/gi, "관찰 후보")
    .replace(/EXPOSURE_WATCH_DYNAMIC_CONTEXT_MATCHED/gi, "관찰 후보")
    .replace(/direct entity/gi, "직접 등장 기업")
    .replace(/business profile/gi, "사업 근거")
    .replace(/stock_business_profile/gi, "사업 근거")
    .replace(/mapped/g, "연결")
    .replace(/\s+/g, " ")
    .trim();

  if (isInternalLine(text)) return "";
  return text;
}

function userReason(value: any, group?: CandidateGroup): string {
  const raw = publicText(value);
  if (!raw) {
    if (group === "direct") return "기사에 직접 등장한 기업이며 사업 연관성이 확인됩니다.";
    if (group === "review") return "기사와 사업 영역이 연결되어 추가 확인이 필요합니다.";
    if (group === "watch") return "직접 등장 기업은 아니지만 관련 테마 노출 가능성이 있습니다.";
    return "";
  }

  return raw
    .replace(/직접 등장 회사\s*\+\s*경제적 이벤트\s*\+\s*사업보고서 사업 객체 연결 확인/g, "기사에 직접 등장한 기업이며 사업 연관성이 확인됩니다.")
    .replace(/직접 등장 회사와 경제적 이벤트는 확인되나 사업 객체 근거가 Direct 수준까지 강하지 않아 Review로 분류/g, "직접 등장 기업이지만 영향 강도는 추가 확인이 필요합니다.")
    .replace(/직접 등장 회사이나 수주·매출 등 Direct 확정 근거는 부족하여 Review로 분류/g, "직접 등장 기업이지만 수주·매출 확정 이벤트는 아니므로 검토 후보입니다.")
    .replace(/직접 등장 회사이나 사업보고서 객체 일치가 약해 관찰로 제한/g, "직접 등장 기업이지만 사업 연결 강도가 낮아 관찰 후보입니다.")
    .replace(/기사 직접 등장 회사는 아니지만 이슈 객체와 사업보고서 근거의 문맥 일치가 확인되어 사업 노출 후보로 표시/g, "기사에 직접 등장한 기업은 아니지만 관련 사업 노출 가능성이 있습니다.")
    .replace(/\s+/g, " ")
    .trim();
}

function sectorLabel(value: any): string {
  const raw = cleanText(value);
  const key = raw.toUpperCase();
  const map: Record<string, string> = {
    SEMICONDUCTOR: "",
    SEM: "",
    MEMORY: "메모리",
    AI: "AI",
    DEFENSE: "방산",
    DEFENSE_AEROSPACE: "방산/항공",
    SHIPBUILDING: "조선",
    AUTOMOTIVE: "자동차",
    EV: "전기차",
    BATTERY: "배터리",
    BIO_HEALTHCARE: "바이오/헬스케어",
    CHIP: "",
    MACRO: "매크로",
    POLICY: "정책",
  };
  if (raw === "반도체" || raw === "칩/반도체" || raw === "반도체/칩/수출") return "";
  return map[key] ?? raw;
}

function getParsed(feed: any) {
  const result =
    parseJsonLoose(feed?.result_json) ??
    parseJsonLoose(feed?.resultJson) ??
    parseJsonLoose(feed?.analysis_result) ??
    parseJsonLoose(feed?.analysisResult) ??
    {};
  const gpt =
    parseJsonLoose(feed?.gpt_analysis) ??
    parseJsonLoose(feed?.gptAnalysis) ??
    feed?.gpt_analysis ??
    feed?.gptAnalysis ??
    {};
  const ticker =
    parseJsonLoose(feed?.ticker_insight) ??
    parseJsonLoose(feed?.tickerInsight) ??
    parseJsonLoose(gpt?.ticker_insight) ??
    parseJsonLoose(gpt?.tickerInsight) ??
    feed?.ticker_insight ??
    feed?.tickerInsight ??
    gpt?.ticker_insight ??
    gpt?.tickerInsight ??
    {};
  const impact =
    parseJsonLoose(feed?.impact_json_raw) ??
    parseJsonLoose(feed?.impactJsonRaw) ??
    parseJsonLoose(gpt?.impact_json_raw) ??
    parseJsonLoose(gpt?.impactJsonRaw) ??
    feed?.impact_json_raw ??
    feed?.impactJsonRaw ??
    gpt?.impact_json_raw ??
    gpt?.impactJsonRaw ??
    result ??
    {};
  const final = result?.final ?? ticker?.final ?? impact?.final ?? gpt?.final ?? {};
  const articleImpact = final?.article_impact ?? final?.articleImpact ?? result?.article_impact ?? ticker?.article_impact ?? impact?.article_impact ?? {};
  return { result, gpt, ticker, impact, final, articleImpact };
}

function getFinalMode(feed: any, parsed?: ReturnType<typeof getParsed>): string {
  const p = parsed ?? getParsed(feed);
  return String(
    feed?.event_final_mode ??
      feed?.eventFinalMode ??
      p.result?.event_final_mode ??
      p.result?.eventFinalMode ??
      p.final?.event_final_mode ??
      p.final?.eventFinalMode ??
      p.ticker?.event_final_mode ??
      p.ticker?.eventFinalMode ??
      p.impact?.event_final_mode ??
      "",
  );
}


function getImpactScore(feed: any, parsed?: ReturnType<typeof getParsed>): number {
  const p = parsed ?? getParsed(feed);
  return clamp100(
    firstNonEmpty(
      feed?.impact_score,
      feed?.impactScore,
      p.result?.impact_score,
      p.final?.impact_score,
      p.articleImpact?.impact_score,
      p.ticker?.impact_score,
      p.impact?.impact_score,
      0,
    ),
  );
}

function getImpactDirection(feed: any, parsed?: ReturnType<typeof getParsed>): "positive" | "negative" | "neutral" {
  const p = parsed ?? getParsed(feed);
  const raw = norm(
    firstNonEmpty(
      feed?.impact_direction,
      feed?.impactDirection,
      p.articleImpact?.impact_direction,
      p.articleImpact?.impactDirection,
      p.final?.impact_direction,
      p.result?.impact_direction,
      "",
    ),
  );
  if (raw.includes("negative") || raw.includes("부정") || raw.includes("악재") || raw.includes("risk")) return "negative";
  if (raw.includes("positive") || raw.includes("긍정") || raw.includes("호재")) return "positive";
  return "neutral";
}

function directionLabel(direction: "positive" | "negative" | "neutral"): string {
  if (direction === "positive") return "긍정";
  if (direction === "negative") return "부정";
  return "중립";
}

function getTitle(feed: FeedItem): string {
  const raw = firstNonEmpty(
    (feed as any).title_ko,
    (feed as any).titleKo,
    (feed as any).title,
    (feed as any).headline,
  );
  return extractTitle(raw) || cleanText(raw) || "(제목 없음)";
}

function getSummary(feed: FeedItem, parsed?: ReturnType<typeof getParsed>): string {
  const p = parsed ?? getParsed(feed);
  const raw = firstNonEmpty(
    (feed as any).summary_ko,
    (feed as any).summaryKo,
    (feed as any).summary,
    p.result?.summary,
    p.gpt?.summary,
    p.final?.impact_reason,
    p.articleImpact?.impact_reason,
    (feed as any).description,
  );
  const summary = extractSummary(raw);
  if (summary && summary !== getTitle(feed)) return summary;
  return summary || "";
}

function getDate(feed: any): string {
  return String(firstNonEmpty(feed?.published_at, feed?.publishedAt, feed?.created_at, feed?.createdAt, ""));
}

function getDateLabel(date: string): string {
  const d = dayjs(date);
  if (!d.isValid()) return "";
  return d.format("YYYY.MM.DD");
}

function getSource(feed: any): string {
  return cleanText(firstNonEmpty(feed?.source_name, feed?.sourceName, feed?.source, feed?.publisher, "출처 미상"));
}

function candidateDecision(raw: any): CandidateGroup {
  const text = norm(
    firstNonEmpty(
      raw?.tier,
      raw?.mapping_decision,
      raw?.mappingDecision,
      raw?.decision,
      raw?.mapping_bucket,
      raw?.mappingBucket,
      raw?.relation_tier,
      raw?.match_type,
      raw?.matchType,
      raw?.role,
    ),
  );

  if (text.includes("exposure") || text.includes("watch") || text.includes("관찰")) return "watch";
  if (text.includes("review") || text.includes("검토")) return "review";
  if (
    text.includes("direct") ||
    text.includes("action") ||
    text.includes("직접") ||
    text === "mapped" ||
    text.includes("stock mapped")
  ) return "direct";
  if (raw?.is_direct_stock_mapping || raw?.isDirectStockMapping) return "direct";
  return "watch";
}

function normalizeCandidate(raw: any, fallback?: CandidateGroup): StockCandidate | null {
  if (!raw) return null;

  if (typeof raw === "string") {
    const ticker = normalizeTicker(raw);
    const name = cleanText(raw.replace(/\(?A?\d{6}\)?/g, "")) || ticker;
    if (!ticker || !name) return null;
    return { name, corp_name: name, ticker, stock_code: ticker, mapping_decision: fallback ?? "watch" };
  }

  if (typeof raw !== "object") return null;

  const ticker = normalizeTicker(raw.ticker ?? raw.stock_code ?? raw.stockCode ?? raw.code);
  const name = cleanText(raw.corp_name ?? raw.corpName ?? raw.name ?? raw.company_name ?? raw.companyName ?? ticker);
  if (!ticker || !name) return null;

  const group = fallback ?? candidateDecision(raw);
  return {
    ...raw,
    name,
    corp_name: name,
    ticker,
    stock_code: ticker,
    mapping_decision: group,
    matched_terms: raw.matched_terms ?? raw.matchedTerms,
    strong_terms: raw.strong_terms ?? raw.strongTerms,
    support_terms: raw.support_terms ?? raw.supportTerms,
  };
}

function isBlockedCandidate(candidate: StockCandidate): boolean {
  if (!normalizeTicker(candidate.ticker ?? candidate.stock_code)) return true;
  if (candidate.user_visible === false) return true;
  const decision = norm(candidate.mapping_decision ?? candidate.decision ?? "");
  if (decision.includes("exclude") || decision.includes("제외")) return true;
  const reasonCode = String(candidate.reason_code ?? "").trim();
  if (INTERNAL_REASON_CODES.has(reasonCode)) return true;
  if (/BLOCKED|DISABLED|AMBIGUOUS|EXCLUDE_CAPPED|WEAK_ENTITY/i.test(reasonCode)) return true;
  return false;
}


function candidateHasRealName(candidate: StockCandidate): boolean {
  const ticker = normalizeTicker(candidate.ticker ?? candidate.stock_code) ?? "";
  const name = cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name);
  if (!name) return false;
  return name !== ticker && !/^\d{6}$/.test(name);
}

function candidateRichness(candidate: StockCandidate): number {
  let score = 0;
  if (candidateHasRealName(candidate)) score += 100;
  if (cleanText(candidate.reason)) score += 20;
  if (asArrayLoose(candidate.matched_terms ?? candidate.matchedTerms).length) score += 10;
  if (asArrayLoose(candidate.strong_terms ?? candidate.strongTerms).length) score += 10;
  if (asArrayLoose(candidate.support_terms ?? candidate.supportTerms).length) score += 6;
  if (candidate.is_direct_stock_mapping || candidate.isDirectStockMapping) score += 8;
  if (candidateDecision(candidate) === "direct") score += 5;
  return score;
}

function mergeCandidate(existing: StockCandidate, incoming: StockCandidate): StockCandidate {
  const preferred =
    candidateRichness(incoming) > candidateRichness(existing)
      ? incoming
      : existing;
  const secondary = preferred === incoming ? existing : incoming;

  const ticker = normalizeTicker(
    preferred.ticker ??
    preferred.stock_code ??
    secondary.ticker ??
    secondary.stock_code,
  );

  const preferredName = cleanText(
    preferred.corp_name ??
    preferred.corpName ??
    preferred.name,
  );
  const secondaryName = cleanText(
    secondary.corp_name ??
    secondary.corpName ??
    secondary.name,
  );
  const name =
    (preferredName && preferredName !== ticker ? preferredName : "") ||
    (secondaryName && secondaryName !== ticker ? secondaryName : "") ||
    preferredName ||
    secondaryName ||
    ticker ||
    "";

  const preferredGroup = candidateDecision(preferred);
  const secondaryGroup = candidateDecision(secondary);
  const group: CandidateGroup =
    preferredGroup === "direct" || secondaryGroup === "direct"
      ? "direct"
      : preferredGroup === "review" || secondaryGroup === "review"
        ? "review"
        : "watch";

  return {
    ...secondary,
    ...preferred,
    ticker,
    stock_code: ticker,
    name,
    corp_name: name,
    corpName: name,
    mapping_decision: group,
    mappingDecision: group,
    matched_terms:
      preferred.matched_terms ??
      preferred.matchedTerms ??
      secondary.matched_terms ??
      secondary.matchedTerms,
    strong_terms:
      preferred.strong_terms ??
      preferred.strongTerms ??
      secondary.strong_terms ??
      secondary.strongTerms,
    support_terms:
      preferred.support_terms ??
      preferred.supportTerms ??
      secondary.support_terms ??
      secondary.supportTerms,
    reason: cleanText(preferred.reason) || cleanText(secondary.reason) || undefined,
  };
}

function collectCandidateSources(feed: any, parsed: ReturnType<typeof getParsed>): StockCandidate[] {
  const sources: Array<{ rows: any[]; fallback?: CandidateGroup }> = [
    { rows: asArrayLoose(feed?.direct_mapped_stocks ?? feed?.directMappedStocks ?? parsed.result?.direct_mapped_stocks ?? parsed.ticker?.direct_mapped_stocks), fallback: undefined },
    { rows: asArrayLoose(feed?.exposure_watch_candidates ?? feed?.exposureWatchCandidates ?? parsed.result?.exposure_watch_candidates ?? parsed.ticker?.exposure_watch_candidates), fallback: "watch" },
    { rows: asArrayLoose(feed?.mapped_ticker_rows_json ?? feed?.mappedTickerRowsJson ?? feed?.mapped_tickers_json ?? feed?.mappedTickersJson), fallback: undefined },
    { rows: asArrayLoose(feed?.mapped_ticker_rows ?? feed?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.mapped_ticker_rows ?? parsed.ticker?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.impact?.mapped_ticker_rows ?? parsed.impact?.mappedTickerRows), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.event_unit_visible_stocks ?? feed?.event_unit_visible_stocks), fallback: undefined },
    { rows: asArrayLoose(parsed.ticker?.direct_candidates), fallback: "direct" },
    { rows: asArrayLoose(parsed.ticker?.review_candidates), fallback: "review" },
    { rows: asArrayLoose(parsed.ticker?.watch_candidates ?? parsed.ticker?.watch), fallback: "watch" },
    { rows: asArrayLoose(parsed.ticker?.direct_tickers), fallback: "direct" },
    { rows: asArrayLoose(parsed.ticker?.indirect_tickers), fallback: "review" },
    { rows: asArrayLoose(parsed.ticker?.related_tickers), fallback: "watch" },
  ];

  const candidateByTicker = new Map<string, StockCandidate>();
  const tickerOrder: string[] = [];

  sources.forEach(({ rows, fallback }) => {
    rows.forEach((row) => {
      const candidate = normalizeCandidate(row, fallback);
      if (!candidate || isBlockedCandidate(candidate)) return;

      const key = normalizeTicker(candidate.ticker ?? candidate.stock_code);
      if (!key) return;

      const existing = candidateByTicker.get(key);
      if (!existing) {
        candidateByTicker.set(key, candidate);
        tickerOrder.push(key);
        return;
      }

      // 동일 ticker가 문자열 배열과 상세 객체에 동시에 존재할 수 있다.
      // 종목명이 ticker 숫자로만 들어온 항목보다 corp_name/name을 가진 상세 객체를 우선한다.
      candidateByTicker.set(key, mergeCandidate(existing, candidate));
    });
  });

  return tickerOrder
    .map((ticker) => candidateByTicker.get(ticker))
    .filter((candidate): candidate is StockCandidate => Boolean(candidate));
}

function bucketCandidates(candidates: StockCandidate[]): CandidateBuckets {
  const direct: StockCandidate[] = [];
  const review: StockCandidate[] = [];
  const watch: StockCandidate[] = [];

  candidates.forEach((candidate) => {
    const group = candidateDecision(candidate);
    if (group === "direct") direct.push(candidate);
    else if (group === "review") review.push(candidate);
    else watch.push(candidate);
  });

  return { direct, review, watch, visible: [...direct, ...review, ...watch] };
}



function candidateName(candidate: StockCandidate): string {
  return cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name ?? candidate.ticker ?? "후보");
}

function candidateKey(candidate: StockCandidate): string {
  return normalizeTicker(candidate.ticker ?? candidate.stock_code) ?? candidateName(candidate);
}




function getTags(feed: any, parsed: ReturnType<typeof getParsed>): string[] {
  const raw =
    parseJsonLoose(feed?.issue_tags) ??
    parseJsonLoose(feed?.issueTags) ??
    parseJsonLoose(parsed.gpt?.issue_tags) ??
    parseJsonLoose(parsed.ticker?.issue_tags) ??
    feed?.issue_tags ??
    feed?.issueTags ??
    [];
  const source = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/[,|]/) : [];
  return source
    .map((x) => sectorLabel(x))
    .filter((x) => x && !isInternalLine(x))
    .slice(0, 6);
}

function modeLabelFrom(feed: any, buckets: CandidateBuckets, parsed: ReturnType<typeof getParsed>): string {
  const mode = getFinalMode(feed, parsed).toUpperCase();
  if (buckets.direct.length > 0) return "직접 영향";
  if (buckets.review.length > 0) return "검토 후보";
  if (buckets.watch.length > 0) return "관찰 후보";
  if (mode.includes("MACRO")) return "매크로 참고";
  if (mode.includes("SECTOR")) return "시장 참고";
  return "매핑 없음";
}

function toneFrom(buckets: CandidateBuckets, direction: "positive" | "negative" | "neutral", impact: number): Tone {
  if (direction === "negative" && (buckets.direct.length > 0 || impact >= 70)) return "risk";
  if (buckets.direct.length > 0) return "direct";
  if (buckets.review.length > 0) return "review";
  if (buckets.watch.length > 0) return "watch";
  if (direction === "positive") return "positive";
  return "neutral";
}

function buildFeedViewModel(feed: FeedItem): FeedViewModel {
  const parsed = getParsed(feed);
  const candidates = collectCandidateSources(feed, parsed);
  const buckets = bucketCandidates(candidates);
  const title = getTitle(feed);
  const summary = getSummary(feed, parsed);
  const date = getDate(feed);
  const impactScore = getImpactScore(feed, parsed);
  const impactDirection = getImpactDirection(feed, parsed);
  const impactReason =
    userReason(
      firstNonEmpty(
        parsed.final?.impact_reason,
        parsed.articleImpact?.impact_reason,
        parsed.result?.impact_reason,
        parsed.gpt?.impact_reason,
        summary,
      ),
    ) || summary;

  const canonicalSector = "";
  const sector = "";

  const id = String(firstNonEmpty((feed as any).id, (feed as any).feed_id, (feed as any).source_feed_id, `${date}-${title}`));
  const modeLabel = modeLabelFrom(feed, buckets, parsed);
  const tone = toneFrom(buckets, impactDirection, impactScore);

  const priority =
    impactScore +
    buckets.direct.length * 45 +
    buckets.review.length * 26 +
    buckets.watch.length * 10 +
    (impactDirection === "negative" ? 8 : 0);

  const dateKey = dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "unknown";
  const clusterTitle = title.toLowerCase().replace(/\[[^\]]+\]|\([^)]+\)/g, " ").replace(/\s+/g, " ").slice(0, 90);

  return {
    id,
    feed,
    title,
    summary,
    source: getSource(feed),
    date,
    dateLabel: getDateLabel(date),
    sectorLabel: sector,
    canonicalSector,
    tags: getTags(feed, parsed),
    impactScore,
    impactDirection,
    impactLabel: directionLabel(impactDirection),
    impactReason,
    modeLabel,
    tone,
    buckets,
    topCandidates: buckets.visible.slice(0, 4),
    priority,
    clusterKey: `${dateKey}:${clusterTitle}`,
  };
}




function shouldShowByMode(vm: FeedViewModel, mode: MappingMode): boolean {
  if (mode === "all") return true;
  if (mode === "direct") return vm.buckets.direct.length > 0;
  if (mode === "review") return vm.buckets.review.length > 0;
  if (mode === "watch") return vm.buckets.watch.length > 0;
  if (mode === "radar") return vm.buckets.visible.length === 0 || /매크로|관찰|참고/.test(vm.modeLabel);
  if (mode === "none") return vm.buckets.visible.length === 0;
  return true;
}

function buildClusters(items: FeedViewModel[]): FeedCluster[] {
  const map = new Map<string, FeedCluster>();
  items.forEach((vm) => {
    const prev = map.get(vm.clusterKey);
    if (!prev) map.set(vm.clusterKey, { key: vm.clusterKey, rep: vm, count: 1, others: [] });
    else {
      prev.count += 1;
      prev.others.push(vm);
      if (vm.priority > prev.rep.priority) prev.rep = vm;
    }
  });
  return Array.from(map.values()).sort((a, b) => b.rep.priority - a.rep.priority || dayjs(b.rep.date).valueOf() - dayjs(a.rep.date).valueOf());
}


function toneClass(tone: Tone | CandidateGroup | "positive" | "negative" | "neutral" | undefined): string {
  if (tone === "direct") return "is-direct";
  if (tone === "review") return "is-review";
  if (tone === "watch") return "is-watch";
  if (tone === "risk" || tone === "negative") return "is-risk";
  if (tone === "positive") return "is-positive";
  return "is-neutral";
}



function modeTabs(): Array<{ key: MappingMode; label: string }> {
  return [
    { key: "all", label: "전체" },
    { key: "direct", label: "직접" },
    { key: "review", label: "검토" },
    { key: "watch", label: "관찰" },
    { key: "radar", label: "참고" },
    { key: "none", label: "매핑 없음" },
  ];
}

function FeedList() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isMobileRoute = location.pathname.startsWith("/m");
  const { sort, feeds = [], loading, error } = useSelector((s: RootState) => s.feeds as any);

  const [selectedFeed, setSelectedFeed] = useState<FeedItem | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mappingMode, setMappingMode] = useState<MappingMode>("all");
  const [dateRange, setDateRange] = useState<"1d" | "3d" | "7d" | "all">("7d");

  const isDesktop = useMediaQuery("(min-width:1180px)");
  const isSmallViewport = !useMediaQuery("(min-width:760px)");
  const isCompact = isMobileRoute || isSmallViewport;

  useEffect(() => {
    dispatch(fetchFeedsStart({ sector: "all", sort }));
  }, [sort, dispatch]);

  const filteredFeeds = useFeedFilters(feeds, {
    impactThreshold: 0,
    dateRange,
    stockSearch: "",
    searchTerm,
    sortType: "latest",
  }) as FeedItem[];

  const viewModels = useMemo(() => filteredFeeds.map(buildFeedViewModel), [filteredFeeds]);

  const displayed = useMemo(() => {
    return viewModels
      .filter((vm) => shouldShowByMode(vm, mappingMode))
      .slice()
      .sort((a, b) => b.priority - a.priority || dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [viewModels, mappingMode]);

  const clusters = useMemo(() => buildClusters(displayed), [displayed]);
  useMemo(() => displayed.slice(0, isDesktop && !isCompact ? 5 : 3), [displayed, isDesktop, isCompact]);

  const stats = useMemo(() => {
    const source = displayed.length ? displayed : viewModels;
    const total = source.length;
    const direct = source.filter((vm) => vm.buckets.direct.length > 0).length;
    const review = source.filter((vm) => vm.buckets.review.length > 0).length;
    const watch = source.filter((vm) => vm.buckets.watch.length > 0).length;
    const avgImpact = total ? Math.round(source.reduce((sum, vm) => sum + vm.impactScore, 0) / total) : 0;
    const risk = source.filter((vm) => vm.impactDirection === "negative" && vm.impactScore >= 60).length;
    return { total, direct, review, watch, avgImpact, risk };
  }, [displayed, viewModels]);

  useMemo(() => {
    const seen = new Set<string>();
    const rows: Array<{ candidate: StockCandidate; vm: FeedViewModel; group: CandidateGroup }> = [];
    displayed.forEach((vm) => {
      ([
        ...vm.buckets.direct.map((x) => [x, "direct"] as const),
        ...vm.buckets.review.map((x) => [x, "review"] as const),
        ...vm.buckets.watch.map((x) => [x, "watch"] as const),
      ]).forEach(([candidate, group]) => {
        const key = `${candidateKey(candidate)}:${group}`;
        if (!key || seen.has(key)) return;
        seen.add(key);
        rows.push({ candidate, vm, group });
      });
    });
    return rows.slice(0, isDesktop ? 10 : 6);
  }, [displayed, isDesktop]);

  const openDetail = useCallback((feed: any) => setSelectedFeed(normalizeForDetail(feed)), []);
  const closeDetail = useCallback(() => setSelectedFeed(null), []);
  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setMappingMode("all");
    setDateRange("7d");
  }, []);

  if (loading && !feeds.length) return <LoadingSpinner />;

  return (
    <>
      <main className={`issue-index ${isMobileRoute ? "is-mobile-route" : ""}`}>
        <div className="issue-index__inner">
          <header className="issue-index__header">
            <div>
              <span className="issue-index__eyebrow">MARKET INTELLIGENCE</span>
              <h1>이슈 리포트</h1>
              <p>시장 영향과 종목 연결 강도를 기준으로 정리한 최신 분석입니다.</p>
            </div>
            <div className="issue-index__stats">
              <span><b>{stats.total}</b> 전체</span>
              <span><b>{stats.avgImpact}</b> 평균 영향도</span>
              <span><b>{stats.direct}</b> 직접</span>
              <span><b>{stats.review + stats.watch}</b> 검토·관찰</span>
            </div>
          </header>

          <section className="issue-index__toolbar" aria-label="이슈 필터">
            <label className="issue-index__search">
              <span>검색</span>
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="종목명, 코드, 키워드"
                aria-label="이슈 검색"
              />
            </label>

            <div className="issue-index__tabs" role="tablist" aria-label="분류 필터">
              {modeTabs().map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={mappingMode === tab.key ? "is-active" : ""}
                  onClick={() => setMappingMode(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <select
              className="issue-index__date"
              value={dateRange}
              onChange={(event) => setDateRange(event.target.value as any)}
              aria-label="기간 선택"
            >
              <option value="1d">1일</option>
              <option value="3d">3일</option>
              <option value="7d">7일</option>
              <option value="all">전체</option>
            </select>

            <button type="button" className="issue-index__reset" onClick={resetFilters}>
              초기화
            </button>
          </section>

          {error ? <div className="issue-index__error">데이터를 불러오지 못했습니다. {String(error)}</div> : null}

          <section className="issue-table-wrap" aria-label="최신 이슈 목록">
            <div className="issue-table-head">
              <span>시각</span>
              <span>이슈</span>
              <span>영향</span>
              <span>분류</span>
              <span>관련 종목</span>
            </div>

            {clusters.length === 0 ? (
              <div className="issue-table-empty">
                <EmptyState
                  variant="noMatch"
                  title="조건에 맞는 이슈가 없습니다"
                  subtitle="검색어를 지우거나 필터를 초기화하세요."
                  actions={[{ label: "필터 초기화", onClick: resetFilters }]}
                />
              </div>
            ) : (
              <div className="issue-table-body">
                {clusters.slice(0, 64).map((cluster) => {
                  const vm = cluster.rep;
                  const candidateNames = vm.topCandidates
                    .slice(0, 3)
                    .map((candidate) => cleanText(candidate.corp_name ?? candidate.corpName ?? candidate.name ?? candidate.ticker))
                    .filter(Boolean);

                  return (
                    <button
                      type="button"
                      className="issue-table-row"
                      key={cluster.key}
                      onClick={() => openDetail(vm.feed)}
                    >
                      <span className="issue-table-row__time">
                        <b>{vm.dateLabel || "-"}</b>
                        <small>
                          {dayjs(vm.date).isValid() && dayjs(vm.date).format("HH:mm") !== "00:00"
                            ? dayjs(vm.date).format("HH:mm")
                            : ""}
                        </small>
                      </span>

                      <span className="issue-table-row__issue">
                        <strong>{vm.title}</strong>
                        <small>{vm.summary || vm.impactReason || "상세 분석을 확인하세요."}</small>
                      </span>

                      <span className={`issue-table-row__impact ${toneClass(vm.tone)}`}>
                        {Math.round(vm.impactScore)}
                      </span>

                      <span className={`issue-table-row__mode ${toneClass(vm.tone)}`}>
                        {vm.modeLabel}
                      </span>

                      <span className="issue-table-row__stocks">
                        {candidateNames.length ? candidateNames.join(" · ") : "연결 종목 없음"}
                        {cluster.count > 1 ? <small>외 {cluster.count - 1}건 유사 이슈</small> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>

      {selectedFeed && <FeedDetailDialog open onClose={closeDetail} feed={selectedFeed as any} />}
    </>
  );
}















export default FeedList;
